import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Footer from "../../Utils/Footer/footer-component";
import SkeletonBuilder from "../../Utils/Skeleton/skeleton-builder-component";
import AdminService from '../../Services/AdminService';
import { Modal } from "../../Utils/Modal/modal-component.js";
import { LoadingAnimation } from "../../Utils/Progress/progress-component.js";
import Layout from "../../Utils/Navbar/navbar-layout-component.js";
import { getAdminStrings } from "../../Public/admin-data.js";
import { ButtonBlack } from "../../Utils/Buttons/button-component.js";
import AeraChart from "../../Utils/Charts/AreaChart.js";
import AdminPrinters from './AdminPrinters';
import AdminConfigurations from './AdminConfigurations';
import AdminLogs from './AdminLogs';
import AdminRestockConfig from './AdminRestockConfig';

// ─── CountUp Hook ──────────────────────────────────────────────────────────────
function useCountUp(target, duration = 1400, triggered = true) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!triggered || !target) return;
        let start = null;
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const step = (ts) => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            setValue(Math.round(ease(progress) * target));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [target, triggered, duration]);
    return value;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, prefix = '', suffix = '', accent = false }) => {
    const counted = useCountUp(typeof value === 'number' ? value : 0, 1400, value > 0);
    const display = typeof value === 'number' ? counted : value;

    return (
        <div className={`rounded-[2rem] p-7 flex flex-col gap-2 ${accent ? 'bg-secondary-500' : 'bg-primary-950'}`}>
            <p className="text-xs font-bold text-white/40">{label}</p>
            <p className="text-5xl text-white leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                {prefix}{display}{suffix}
            </p>
        </div>
    );
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    paid:               { color: 'text-secondary-500', dot: 'bg-secondary-500', badge: 'bg-white text-secondary-500 border-secondary-300',      bar: 'bg-secondary-400' },
    received:           { color: 'text-teal-600',      dot: 'bg-teal-400',     badge: 'bg-teal-50 text-teal-600 border-teal-200',               bar: 'bg-teal-400' },
    processing:         { color: 'text-yellow-600',    dot: 'bg-yellow-400',   badge: 'bg-yellow-50 text-yellow-700 border-yellow-200',          bar: 'bg-yellow-400' },
    ready_for_shipping: { color: 'text-indigo-600',    dot: 'bg-indigo-400',   badge: 'bg-indigo-50 text-indigo-600 border-indigo-200',          bar: 'bg-indigo-400' },
    ready_for_pickup:   { color: 'text-purple-600',    dot: 'bg-purple-400',   badge: 'bg-purple-50 text-purple-600 border-purple-200',          bar: 'bg-purple-400' },
    shipped:            { color: 'text-blue-600',      dot: 'bg-blue-400',     badge: 'bg-blue-50 text-blue-600 border-blue-200',                bar: 'bg-blue-400' },
    picked_up:          { color: 'text-emerald-600',   dot: 'bg-emerald-400',  badge: 'bg-emerald-50 text-emerald-600 border-emerald-200',       bar: 'bg-emerald-400' },
    delivered:          { color: 'text-green-600',     dot: 'bg-green-400',    badge: 'bg-green-50 text-green-600 border-green-200',             bar: 'bg-green-400' },
    done:               { color: 'text-green-700',     dot: 'bg-green-500',    badge: 'bg-green-100 text-green-700 border-green-300',            bar: 'bg-green-500' },
    payment_failed:     { color: 'text-red-600',       dot: 'bg-red-400',      badge: 'bg-red-50 text-red-600 border-red-200',                   bar: 'bg-red-400' },
};

const ACTIVE_STATUSES    = new Set(['paid', 'received', 'processing', 'ready_for_shipping', 'ready_for_pickup', 'shipped', 'picked_up', 'delivered','completed']);
const COMPLETED_STATUSES = new Set(['done']);
const FAILED_STATUSES    = new Set(['payment_failed']);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status, label, config }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${config.badge}`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${config.dot}`} />
        {label}
    </span>
);

// ─── Order Row (desktop table) ────────────────────────────────────────────────
const OrderRow = ({ order, onSelect, statusLabels }) => {
    const config     = STATUS_CONFIG[order.status] || STATUS_CONFIG.paid;
    const label      = statusLabels[order.status] || order.status;
    const date       = new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    const itemCount  = (order.items || []).length;
    const firstImage = order.items?.[0]?.image;

    return (
        <tr
            onClick={() => onSelect(order)}
            className="border-b border-gray-50 hover:bg-gray-50/80 cursor-pointer transition-colors group"
        >
            <td className="py-4 pl-5 pr-4">
                <div className="flex items-center gap-3">
                    {firstImage ? (
                        <img
                            src={firstImage}
                            alt=""
                            className="w-10 h-10 rounded-xl object-cover border border-gray-100 shrink-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
                        />
                    ) : null}
                    <div
                        className={`w-10 h-10 rounded-xl ${config.bar} opacity-60 shrink-0 ${firstImage ? 'hidden' : 'block'}`}
                    />
                    <span className="text-[10px] font-medium text-gray-400">
                        {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </span>
                </div>
            </td>
            <td className="py-4 px-4">
                <div className="flex flex-col gap-0.5">
                    {order.shippingName && (
                        <span className="text-xs font-bold text-primary-950 truncate max-w-[180px]">{order.shippingName}</span>
                    )}
                    <span className="text-[11px] font-medium text-gray-400 truncate max-w-[180px]">{order.email}</span>
                    {order.phone && (
                        <span className="text-[10px] font-medium text-gray-300">{order.phone}</span>
                    )}
                </div>
            </td>
            <td className="py-4 px-4">
                <StatusBadge status={order.status} label={label} config={config} />
            </td>
            <td className="py-4 px-4 text-right">
                <span className="text-sm font-black text-primary-950 italic">${order.total?.toFixed(2)}</span>
            </td>
            <td className="py-4 px-4 text-right">
                <span className="text-[11px] font-medium text-gray-400">{date}</span>
            </td>
        </tr>
    );
};

// ─── Order Card (mobile) ──────────────────────────────────────────────────────
const OrderCardMobile = ({ order, onSelect, statusLabels }) => {
    const config     = STATUS_CONFIG[order.status] || STATUS_CONFIG.paid;
    const label      = statusLabels[order.status] || order.status;
    const date       = new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    const itemCount  = (order.items || []).length;
    const firstImage = order.items?.[0]?.image;

    return (
        <div
            onClick={() => onSelect(order)}
            className="bg-white rounded-2xl border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
        >
            <div className="p-4 flex flex-col gap-3">
                {/* Top row */}
                <div className="flex items-start gap-3">
                    {/* Product image / color swatch */}
                    {firstImage ? (
                        <img
                            src={firstImage}
                            alt=""
                            className="w-12 h-12 rounded-xl object-cover border border-gray-100 shrink-0"
                        />
                    ) : (
                        <div className={`w-12 h-12 rounded-xl ${config.bar} opacity-60 shrink-0`} />
                    )}
                    <div className="flex flex-1 items-start justify-between gap-2 min-w-0">
                        <div className="flex flex-col gap-0.5 min-w-0">
                            {order.shippingName && (
                                <span className="text-sm font-bold text-primary-950 truncate">{order.shippingName}</span>
                            )}
                            <span className="text-[11px] font-medium text-gray-400 truncate">{order.email}</span>
                            {order.phone && (
                                <span className="text-[10px] font-medium text-gray-300">{order.phone}</span>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-base font-black text-primary-950 italic">${order.total?.toFixed(2)}</span>
                            <span className="text-[10px] font-medium text-gray-300">
                                {itemCount} item{itemCount !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </div>
                </div>
                {/* Bottom row */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                    <StatusBadge status={order.status} label={label} config={config} />
                    <div className="flex flex-col items-end gap-0.5">
                        <span className="text-[9px] font-black font-mono text-gray-200">#{order.id.slice(-8).toUpperCase()}</span>
                        <span className="text-[10px] font-medium text-gray-400">{date}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ eyebrow, title, meta }) => (
    <div className="px-8 pt-8 pb-6 flex items-center justify-between border-b border-gray-50">
        <div>
            <p className="text-xs font-bold text-primary-950/30 mb-1">{eyebrow}</p>
            <h3 className="text-3xl text-primary-950 uppercase leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                {title}
            </h3>
        </div>
        {meta && <span className="text-xs font-bold text-gray-400">{meta}</span>}
    </div>
);

// ─── Main Admin Screen ────────────────────────────────────────────────────────
function Admin() {
    const products = useSelector((state) => Array.isArray(state.cart?.products) ? state.cart.products : []);
    const loadingMessage = useSelector((state) => state.loading);
    const systemDataRedux = useSelector((s) => s.systemData || {});

    const lang = systemDataRedux.language;
    const dir = systemDataRedux.dir;
    const links = systemDataRedux.links || [];
    const t = getAdminStrings(lang);

    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [systemDataSections, setSystemDataSections] = useState([]);
    const [systemData, setSystemData] = useState({});

    const [orders, setOrders]               = useState([]);
    const [zohoCustomers, setZohoCustomers] = useState([]);
    const [dataLoading, setDataLoading]     = useState(true);
    const [usersPage, setUsersPage]         = useState(0);

    const USERS_PER_PAGE = 10;

    // ── Fetch orders + Zoho customers ──
    useEffect(() => {
        window.scrollTo(0, 0);
        Promise.all([
            AdminService.getOrders(),
            AdminService.getZohoCustomers(),
        ]).then(([o, c]) => {
            setOrders(o);
            setZohoCustomers(c);
        }).finally(() => setDataLoading(false));
    }, []);

    // ── Derived stats ──
    const paidOrders   = useMemo(() => (Array.isArray(orders) ? orders : []).filter(o => o.status !== 'payment_failed'), [orders]);
    const totalRevenue = useMemo(() => paidOrders.reduce((s, o) => s + (o.total || 0), 0), [paidOrders]);
    const avgOrder     = useMemo(() => paidOrders.length ? totalRevenue / paidOrders.length : 0, [totalRevenue, paidOrders]);

    // ── Chart data ──
    const chartData = useMemo(() => {
        const sorted = [...orders]
            .filter(o => o.status !== 'payment_failed' && o.createdAt)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        const byDay = {};
        sorted.forEach(o => {
            const key = o.createdAt.slice(0, 10);
            if (!byDay[key]) byDay[key] = { clickTime: o.createdAt, revenue: 0, profit: 0 };
            byDay[key].revenue += o.total || 0;
            byDay[key].profit += (o.total || 0) * 0.35;
        });
        return Object.values(byDay).slice(-21);
    }, [orders]);

    // ── System sections ──
    useEffect(() => {
        const fetchSystemSections = async () => {
            try {
                const response = await AdminService.getSystemDataPaths();
                if (response && typeof response === 'object') {
                    setSystemData(response);
                    const paths = [];
                    const traverse = (obj, prefix = '') => {
                        for (const key in obj) {
                            const fullPath = prefix ? `${prefix}.${key}` : key;
                            if (Array.isArray(obj[key]) || typeof obj[key] !== 'object' || obj[key] === null) {
                                paths.push(fullPath);
                            } else {
                                traverse(obj[key], fullPath);
                            }
                        }
                    };
                    traverse(response);
                    setSystemDataSections(paths);
                }
            } catch {
                setSystemDataSections([]);
                setSystemData({});
            }
        };
        fetchSystemSections();
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timeout);
    }, []);

    const ADMIN_PREVIEW_BASE = 'https://sawpy-assets.s3.us-east-2.amazonaws.com/images';
    const adminPreviewMap = {
        'images.gallery': `${ADMIN_PREVIEW_BASE}/flowma_admin_gallery.jpeg`,
        'images.carousel': `${ADMIN_PREVIEW_BASE}/flowma_admin_carousel.jpeg`,
        'images.partners': `${ADMIN_PREVIEW_BASE}/flowma_admin_partners.jpeg`,
        'images.popup': `${ADMIN_PREVIEW_BASE}/flowma_admin_popup.jpeg`,
        'images.specials': `${ADMIN_PREVIEW_BASE}/flowma_admin_specials.jpeg`,
        'content.infoCards': `${ADMIN_PREVIEW_BASE}/flowma_admin_info.jpeg`,
        'content.reviewsData': `${ADMIN_PREVIEW_BASE}/flowma_admin_reviews.jpeg`,
        'content.businessInfo': `${ADMIN_PREVIEW_BASE}/flowma_admin_business_info.jpeg`,
        'content.bannerMessages': `${ADMIN_PREVIEW_BASE}/flowma_admin_banner_messages.jpeg`,
        'products': `${ADMIN_PREVIEW_BASE}/flowma_admin_products.jpeg`,
    };

    const getPreviewForSection = (sectionData) => {
        if (!sectionData) return null;
        if (Array.isArray(sectionData)) {
            const o = sectionData.find(x => x && typeof x === 'object' && (x.image || x.cover || x.url));
            if (o) return o.image || o.cover || o.url;
            return sectionData.find(x => typeof x === 'string' && /^https?:\/\//i.test(x)) || null;
        }
        if (typeof sectionData === 'object') return sectionData.image || sectionData.cover || sectionData.url || null;
        return null;
    };

    return (
        <div className="min-h-screen bg-gray-50 text-neutral-900 font-primary">
            <Modal expand={loadingMessage.visible} timeout={true}>
                <LoadingAnimation loadingMessage={loadingMessage.message} />
            </Modal>

            <Layout>
                <div dir={dir} className={`max-w-7xl mx-auto px-6 py-12 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>

                    {/* ── PAGE HEADER ── */}
                    <div className="flex items-center gap-4 mb-12 flex-wrap">
                        <h1
                            className="text-6xl font-black  lg:text-7xl text-primary-950 uppercase leading-none "
                        >
                            {t.dashboardTitle}
                        </h1>
                    </div>

                    {/* ── STAT CARDS ── */}
                    {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {dataLoading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="bg-primary-950 rounded-[2rem] p-7 animate-pulse">
                                    <div className="h-3 bg-white/10 rounded mb-4 w-1/2" />
                                    <div className="h-12 bg-white/10 rounded w-3/4" />
                                </div>
                            ))
                        ) : (
                            <>
                                <StatCard label={t.stats.revenue} value={Math.round(totalRevenue)} prefix="$" accent />
                                <StatCard label={t.stats.orders} value={paidOrders.length} />
                                <StatCard label={t.stats.customers} value={zohoCustomers.length} />
                                <StatCard label={t.stats.avgOrder} value={Math.round(avgOrder)} prefix="$" />
                            </>
                        )}
                    </div> */}

                    {/* ── REVENUE CHART ── */}
                    {/* {chartData.length > 1 && (
                        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm mb-8 overflow-hidden">
                            <div className="px-8 pt-8 pb-2 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-primary-950/30 mb-1">{t.chart.eyebrow}</p>
                                    <h3 className="text-3xl text-primary-950 uppercase leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                                        {t.chart.title}
                                    </h3>
                                </div>
                                <span className="text-xs font-bold text-gray-400 border border-gray-100 rounded-full px-3 py-1">
                                    {t.chart.lastDays(chartData.length)}
                                </span>
                            </div>
                            <AeraChart chartData={chartData} interval="DAY" />
                        </div>
                    )} */}

                    {/* ── ORDER GRIDS ── */}
                    {/* {(() => {
                        const activeOrders    = [...orders].filter(o => ACTIVE_STATUSES.has(o.status)).reverse();
                        const completedOrders = [...orders].filter(o => COMPLETED_STATUSES.has(o.status)).reverse();
                        const failedOrders    = [...orders].filter(o => FAILED_STATUSES.has(o.status)).reverse();

                        const OrderTable = ({ rows, eyebrow, title, accent }) => rows.length === 0 ? null : (
                            <div className={`rounded-[2rem] border shadow-sm mb-8 overflow-hidden ${accent ? 'bg-red-50 border-red-100' : 'bg-white border-gray-100'}`}>
                                <SectionHeader eyebrow={eyebrow} title={`${title} (${rows.length})`} />

                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-50">
                                                {t.ordersTable.headers.map((h, i) => (
                                                    <th key={i} className={`py-3 px-4 text-xs font-semibold text-gray-400 ${i >= 3 ? 'text-right' : 'text-left'} ${i === 0 ? 'pl-5' : ''}`}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(order => (
                                                <OrderRow
                                                    key={order.id}
                                                    order={order}
                                                    onSelect={(o) => navigate(`/admin/order/${o.id}`)}
                                                    statusLabels={t.statusLabels}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="block md:hidden p-4 flex flex-col gap-3">
                                    {rows.map(order => (
                                        <OrderCardMobile
                                            key={order.id}
                                            order={order}
                                            onSelect={(o) => navigate(`/admin/order/${o.id}`)}
                                            statusLabels={t.statusLabels}
                                        />
                                    ))}
                                </div>
                            </div>
                        );

                        return (
                            <>
                                <OrderTable rows={activeOrders}    eyebrow={t.ordersTable.activeEyebrow}    title={t.ordersTable.activeTitle} />
                                <OrderTable rows={completedOrders} eyebrow={t.ordersTable.completedEyebrow} title={t.ordersTable.completedTitle} />
                                <OrderTable rows={failedOrders}    eyebrow={t.ordersTable.failedEyebrow}    title={t.ordersTable.failedTitle} accent />
                            </>
                        );
                    })()} */}

                    {/* ── ZOHO CUSTOMERS ── */}
                    {/* {zohoCustomers.length > 0 && (
                        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm mb-8 overflow-hidden">
                            <SectionHeader
                                eyebrow={t.usersTable.eyebrow}
                                title={t.usersTable.title}
                                meta={t.usersTable.registered(zohoCustomers.length)}
                            />
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-50">
                                            {t.usersTable.headers.map((h, i) => (
                                                <th key={i} className="py-3 px-4 text-left text-xs font-semibold text-gray-400">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {zohoCustomers.slice(usersPage * USERS_PER_PAGE, (usersPage + 1) * USERS_PER_PAGE).map((c, i) => {
                                            const customerOrders = orders.filter(o => o.email?.toLowerCase() === c.email?.toLowerCase());
                                            return (
                                                <tr key={c.contact_id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-full bg-primary-950 flex items-center justify-center text-white text-[8px] font-black shrink-0">
                                                                {(c.contact_name || c.email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                                            </div>
                                                            <span className="text-[11px] font-bold text-primary-950">{c.contact_name || '—'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="text-[11px] font-medium text-gray-400 truncate block max-w-[200px]">{c.email || '—'}</span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="text-[11px] font-bold text-primary-950">{customerOrders.length}</span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <span className="text-[11px] font-medium text-gray-400">{c.phone || '—'}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {zohoCustomers.length > USERS_PER_PAGE && (() => {
                                const totalPages = Math.ceil(zohoCustomers.length / USERS_PER_PAGE);
                                const start = usersPage * USERS_PER_PAGE + 1;
                                const end   = Math.min((usersPage + 1) * USERS_PER_PAGE, zohoCustomers.length);
                                return (
                                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50">
                                        <span className="text-[11px] font-medium text-gray-400">
                                            {start}–{end} of {zohoCustomers.length}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setUsersPage(p => Math.max(0, p - 1))}
                                                disabled={usersPage === 0}
                                                className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:border-primary-950 hover:text-primary-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            {Array.from({ length: totalPages }).map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setUsersPage(idx)}
                                                    className={`w-7 h-7 rounded-lg text-[11px] font-black transition-all
                                                        ${idx === usersPage
                                                            ? 'bg-primary-950 text-white'
                                                            : 'border border-gray-200 text-gray-400 hover:border-primary-950 hover:text-primary-950'}`}
                                                >
                                                    {idx + 1}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => setUsersPage(p => Math.min(totalPages - 1, p + 1))}
                                                disabled={usersPage === totalPages - 1}
                                                className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:border-primary-950 hover:text-primary-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )} */}

                    {/* ── CONTENT EDITOR CARDS ── */}
                    <div className="mb-6">
                        <p className="text-xs font-bold text-primary-950/30 mb-6">{t.contentEditor.eyebrow}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {loading ? (
                                Array.from({ length: 4 }).map((_, idx) => (
                                    <div key={idx} className="bg-white rounded-2xl p-8 border border-gray-100 animate-pulse">
                                        <SkeletonBuilder type="LargeText" count={1} />
                                        <SkeletonBuilder type="SmallText" count={2} />
                                    </div>
                                ))
                            ) : (
                                <>
                                    {/* {products.length > 0 && (
                                        <div className="group bg-white rounded-2xl border border-gray-100 flex flex-col h-full overflow-hidden transition-all hover:border-primary-950/20 hover:shadow-sm">
                                            <div className="aspect-video w-full bg-gray-50 border-b border-gray-100 relative overflow-hidden">
                                                <img src={adminPreviewMap.products} alt="products" className="w-full h-full object-cover" />
                                                <div className="absolute top-3 left-3 bg-white/90 px-2 py-1 rounded-md text-[9px] font-black uppercase  border border-gray-200">
                                                    {t.contentEditor.inventoryLabel}
                                                </div>
                                            </div>
                                            <div className="p-5 flex flex-grow flex-col">
                                                <h2 className="text-base font-bold truncate capitalize mb-1">{t.products.title}</h2>
                                                <p className="text-xs text-gray-400 font-medium mb-4">{t.products.totalCount}: {products.length}</p>
                                                <div className="mt-auto">
                                                    <ButtonBlack onClick={() => navigate('/product-editor')} className="w-full !py-3 !text-[11px] font-bold !rounded-xl">
                                                        {t.products.openEditor}
                                                    </ButtonBlack>
                                                </div>
                                            </div>
                                        </div>
                                    )} */}

                                    {systemDataSections.map((section) => {
                                        const sectionData = section.split('.').reduce((acc, key) => acc && acc[key], systemData);
                                        const sectionKey = section.split('.').pop();
                                        const previewSrc = adminPreviewMap[section] || getPreviewForSection(sectionData);

                                        return (
                                            <div key={section} className="group bg-white rounded-2xl border border-gray-100 flex flex-col h-full overflow-hidden transition-all hover:border-primary-950/20 hover:shadow-sm">
                                                <div className="aspect-video w-full bg-gray-50 border-b border-gray-100 relative overflow-hidden">
                                                    {previewSrc
                                                        ? <img src={previewSrc} alt={sectionKey} className="w-full h-full object-cover" />
                                                        : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-400">{t.contentEditor.noPreview}</div>
                                                    }
                                                </div>
                                                <div className="p-5 flex flex-col flex-grow">
                                                    <h2 className="text-base font-bold truncate capitalize mb-1">{sectionKey}</h2>
                                                    <p className="text-xs text-gray-400 font-medium mb-4 truncate">
                                                        {section.split('.').slice(0, -1).join(' / ')}
                                                    </p>
                                                    <div className="flex flex-col gap-2 mb-6 flex-grow">
                                                        <div className="flex justify-between items-center text-xs pb-1.5 border-b border-gray-50">
                                                            <span className="font-medium text-gray-400">{t.systemSections.items}</span>
                                                            <span className="font-bold">{Array.isArray(sectionData) ? sectionData.length : 1}</span>
                                                        </div>
                                                    </div>
                                                    <ButtonBlack onClick={() => navigate(`/system-editor/${section}`)} className="w-full !py-3 !text-[11px] font-bold !rounded-xl">
                                                        {t.systemSections.edit}
                                                    </ButtonBlack>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── RESTOCK CONFIGURATION ── */}
                    {/* <AdminRestockConfig t={t.restock} /> */}

                    {/* ── PRINTERS ── */}
                    {/* <AdminPrinters t={t.printers} links={links} /> */}


                    {/* ── CONFIGURATIONS ── */}
                    <AdminConfigurations t={t.configurations} />

                    {/* ── LOGS ── */}
                    <AdminLogs t={t.logs} dir={dir} />

                </div>
            </Layout>
            <Footer />
        </div>
    );
}

export default Admin;
